import { useState, useContext, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AuthContext from "../../../context/auth/AuthContext";
import useTranslator from "../../../hooks/useTranslator";
import {
  getUserDisplayName,
  haveValue,
  placeNotificationLink,
  toast,
} from "../../../helper/common";
import { calculateNowTime } from "../../../services/sharedService";
import NotificationGroups from "../../../components/NotificationGroups";
import {
  Card,
  Icon,
  PageHeader,
  SkeletonList,
  Tabs,
} from "../../../components/design";
import "./Notification.css";

// Older notifications carry their link in the text instead of their type.
const legacyLink = (notification) => {
  if (notification?.body?.includes("basic"))
    return `/tenant-basic-profile/${notification._id}`;
  if (notification?.body?.includes("sent"))
    return `/send-link?sharedprofileid=${notification?.shared_profile_id?._id}`;
  return undefined;
};

const Notification = () => {
  const { _t } = useTranslator();
  const { hash } = useLocation();
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  const { notifications, userType, notificationSeen, loadMyNotifications } =
    useContext(AuthContext);

  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const all = notifications || [];
  const unread = all.filter((item) => !item?.marked_read);
  const visibleNotifications = filter === "unread" ? unread : all;

  useEffect(() => {
    const messages = {
      "#already_awarded": "youAreAlreadyAwardedAProperty",
      "#no_longer_available": "thisPropertyIsNoLongerAvailable",
    };
    if (messages[hash]) {
      toast.error(_t(messages[hash]));
      navigate(
        haveValue(workspaceId)
          ? `/${workspaceId}/notification`
          : "/notification"
      );
    }
  }, [hash]);

  useEffect(() => {
    loadMyNotifications(localStorage.getItem("saveduserid"), userType).finally(
      () => setLoading(false)
    );
  }, []);

  // Insurance contract reminders are sent by Skor itself
  const isInsurancePendingContract = (notification) =>
    [
      "insurance_pending_contract",
      "insurance_pending_contract_reminder",
    ].includes(notification?.notification_type);

  const insuranceTitle = (notification) => {
    switch (notification?.notification_type) {
      case "insurance_pending_contract":
        return `${_t("paymentReceivedFor")} ${notification?.body}. ${_t("addBeneficiaryChoosePolicyStartAndUploadContract")}`;
      case "insurance_pending_contract_reminder":
        return `${_t("reminderCompleteInsuranceFor")} ${notification?.body} ${_t("byAddingBeneficiaryStartDateAndContract")}`;
      default:
        return undefined;
    }
  };

  // NotificationGroups shows getNotificationDisplayText, which falls back to
  // the body for these types, so put the full text in the body.
  const displayedNotifications = visibleNotifications.map((notification) =>
    isInsurancePendingContract(notification)
      ? { ...notification, body: insuranceTitle(notification) }
      : notification
  );

  // who sent it: the landlord for tenants, otherwise the tenant
  const fromLandlord = (notification) =>
    userType === "tenant" &&
    notification?.notification_type !== "guarantor_declined";

  const senderName = (notification) =>
    isInsurancePendingContract(notification)
      ? "Skor"
      : fromLandlord(notification)
        ? `${notification?.landlord_fname ?? ""} ${
            notification?.landlord_lname ?? ""
          }`.trim()
        : getUserDisplayName(notification?.created_by_tenant, "");

  const senderRole = (notification) =>
    fromLandlord(notification)
      ? notification?.created_by_landlord?.landlordtype === "agent"
        ? _t("agent")
        : _t("landlord")
      : _t("tenant");

  const openNotification = (notification) => {
    if (notification?.shared_profile_id?.pendingDecision === true) {
      toast.error(_t("pendingDecisionFromTenant"));
      return;
    }
    notificationSeen(notification?._id);
    const link =
      placeNotificationLink(notification, workspaceId) ??
      legacyLink(notification);
    if (link) navigate(link);
  };

  return (
    <div className="main-page-wrapper notifications-page">
      <PageHeader title={_t("notifications")} />
      <Card className="notifications-page__card">
        <Tabs
          className="notifications-page__tabs"
          value={filter}
          onChange={setFilter}
          items={[
            {
              value: "all",
              label: _t("all"),
              count: loading ? undefined : all.length,
            },
            {
              value: "unread",
              label: _t("unread"),
              count: loading ? undefined : unread.length,
            },
          ]}
        />
        {loading && visibleNotifications.length === 0 ? (
          <SkeletonList
            rows={5}
            variant="card"
            avatarSize={32}
            className="notifications-page__skeleton"
            label={_t("loading")}
          />
        ) : visibleNotifications.length === 0 ? (
          <div className="notifications-page__empty">
            <span className="notifications-page__empty-icon">
              <Icon name="bell" size={24} />
            </span>
            <p>{_t("youHaveNotReceivedAnyNotificationsYet")}</p>
          </div>
        ) : (
          <NotificationGroups
            notifications={displayedNotifications}
            getAvatar={(notification) =>
              senderName(notification)?.charAt(0)?.toUpperCase()
            }
            getMeta={(notification) => (
              <span className="notifications-page__meta">
                {senderName(notification) && (
                  <>
                    <span>
                      {senderName(notification)}
                      {!isInsurancePendingContract(notification) &&
                        ` · ${senderRole(notification)}`}
                    </span>
                    <span className="notification-meta-dot" />
                  </>
                )}
                <span>
                  {calculateNowTime(
                    notification?.createdAt,
                    localStorage.getItem("i18nextLng")
                  )}
                </span>
              </span>
            )}
            onOpen={(notification) =>
              openNotification(
                all.find((item) => item?._id === notification?._id) ??
                  notification
              )
            }
          />
        )}
      </Card>
    </div>
  );
};

export default Notification;
